import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, Organization } from '@/types'
import { authService, type MyOrganization } from '@/services/auth'
import { organizationsApi, setRequestActiveOrg } from '@/lib/api'
import { PENDING_SELECTION_MESSAGE } from '@/lib/lockout'

export { PENDING_SELECTION_MESSAGE }

/**
 * Decode a JWT payload without verification (the server already verified it
 * when it was issued).  Returns null if the token is malformed or expired.
 */
function decodeJwt(token: string): Record<string, any> | null {
  try {
    const base64 = token.split('.')[1]
    if (!base64) return null
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json)

    // Check expiry
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      return null // expired
    }
    return payload
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const organization = ref<Organization | null>(null)
  const accessToken = ref<string | null>(null)
  const loading = ref(false)
  const subscriptionLockedOut = ref<string | null>(null) // null = not locked, string = lock reason/message

  // Multi-org: the orgs this user belongs to + which one is active. The active
  // org id is persisted and sent as the x-org-id header on every request.
  const memberships = ref<MyOrganization[]>([])
  const activeOrgId = ref<string | null>(localStorage.getItem('activeOrgId'))
  const activeOrg = computed(() => memberships.value.find((m) => m.orgId === activeOrgId.value) || null)
  const hasMultipleOrgs = computed(() => memberships.value.length > 1)

  // Bumped once after an org change has fully settled (new header + refreshed
  // user/org + reset stores). The main layout keys its <router-view> on this so
  // the active view remounts EXACTLY ONCE — after everything is consistent —
  // instead of racing a mid-switch remount. See MainLayout.vue.
  const orgViewVersion = ref(0)
  const bumpOrgView = () => { orgViewVersion.value++ }

  const setActiveOrg = (orgId: string | null) => {
    activeOrgId.value = orgId
    if (orgId) localStorage.setItem('activeOrgId', orgId)
    else localStorage.removeItem('activeOrgId')
    // Mirror into the axios layer's per-tab header source so the x-org-id header can't
    // be hijacked by another tab writing the shared localStorage key. See lib/api.ts.
    setRequestActiveOrg(orgId)
  }

  const isAuthenticated = computed(() => !!accessToken.value)
  const isRootUser = computed(() => user.value?.role === 'root' || user.value?.isRoot === true)
  const isSuperAdmin = computed(() => ['super_admin', 'owner', 'root'].includes(user.value?.role || ''))
  const isAdmin = computed(() => ['admin', 'super_admin', 'owner', 'root'].includes(user.value?.role || ''))
  const isOwner = computed(() => ['owner', 'root'].includes(user.value?.role || ''))
  const isMember = computed(() => user.value?.role === 'member')
  const isKam = computed(() => user.value?.role === 'kam')
  const isMemberOrKam = computed(() => ['member', 'kam'].includes(user.value?.role || ''))
  const canManagePipelines = computed(() => isAdmin.value)
  const canAccessSettings = computed(() => isSuperAdmin.value)

  /**
   * Initialize auth from localStorage.  Decodes the JWT locally (instant,
   * no network call) so the app can boot immediately even when many tabs
   * are opened at once.  Full user + org data is fetched in the background.
   */
  const initializeAuth = async () => {
    const token = localStorage.getItem('accessToken')
    if (!token) return

    const payload = decodeJwt(token)
    if (!payload || !payload.sub) {
      // Token is expired or malformed — clear it
      localStorage.removeItem('accessToken')
      return
    }

    accessToken.value = token

    // Multi-org: ensure an active org is selected (persisted one, else the token's).
    if (!activeOrgId.value && payload.orgId) setActiveOrg(payload.orgId)

    // Immediately populate user from JWT payload (no API call)
    user.value = {
      _id: payload.sub,
      email: payload.email || '',
      name: payload.name || '',
      role: payload.role || 'member',
      status: 'active',
      orgId: payload.orgId || '',
      isRoot: payload.isRoot,
      createdAt: '',
      updatedAt: '',
    } as User

    // Fetch full user + org data in the background (non-blocking).
    // If this fails the app still works with JWT-decoded data.
    hydrateFullUserData().catch(() => {})
  }

  /** Background fetch of full user profile and organization data. */
  const hydrateFullUserData = async () => {
    try {
      const userData = await authService.getCurrentUser()
      user.value = userData

      // Multi-org: load the org list for the switcher (non-blocking failure ok).
      fetchMyOrganizations()

      // Fetch the ACTIVE org's data (skip for root user — orgId is '*'). It may
      // differ from the user's primary org when a ?org= deep-link selected another
      // org: using the active id keeps this in step with the x-org-id header and
      // avoids a 403 if the guard switches orgs while this background hydration is
      // in flight.
      const orgToLoad = activeOrgId.value || userData.orgId
      if (orgToLoad && orgToLoad !== '*' && userData.role !== 'root') {
        const orgResponse = await organizationsApi.get(orgToLoad)
        organization.value = orgResponse.data
      }

      // Proactive lockout detection
      if (organization.value?.subscription?.pendingUserSelection && userData.role !== 'owner') {
        subscriptionLockedOut.value = PENDING_SELECTION_MESSAGE
      }
    } catch (error: any) {
      if (subscriptionLockedOut.value) return
      console.warn('[auth] background hydration failed:', error?.message || error)
    }
  }

  let orgLoadPromise: Promise<Organization | null> | null = null
  /**
   * Guarantee organization.value is loaded (or determined absent) and return it.
   * initializeAuth hydrates the org in the BACKGROUND (hydrateFullUserData is not
   * awaited), so on a hard page-load a route guard can run before organization is
   * set. Guards that gate on org fields (e.g. requiresWhatsapp) must await this
   * instead of reading the ref directly, or they redirect on a null-during-hydration
   * race. Concurrent callers share one in-flight request.
   */
  const ensureOrganizationLoaded = async (): Promise<Organization | null> => {
    if (organization.value) return organization.value
    const orgToLoad = activeOrgId.value || user.value?.orgId
    if (!orgToLoad || orgToLoad === '*' || user.value?.role === 'root') return null
    if (!orgLoadPromise) {
      orgLoadPromise = organizationsApi
        .get(orgToLoad)
        .then((res) => {
          organization.value = res.data
          return organization.value
        })
        .catch(() => organization.value)
        .finally(() => {
          orgLoadPromise = null
        })
    }
    return orgLoadPromise
  }

  /**
   * "Try again" on the locked-out screen: re-run the org load the login path uses. Resolves true
   * (and clears `subscriptionLockedOut`) when the org loads and nothing still locks this user out;
   * false when it is still locked — the API refused with a lockout 403 (lib/api.ts re-sets the
   * message) or the owner still has to pick who stays (same rule as login). Rejects with the
   * error when it couldn't tell (network, 5xx, any other refusal), so the screen can say "couldn't
   * check" instead of "still paused". The lock stays set while the request runs, so the app
   * behind never flashes into view.
   */
  const retryLockout = async (): Promise<boolean> => {
    const orgToLoad = activeOrgId.value || user.value?.orgId
    if (!orgToLoad || orgToLoad === '*' || user.value?.role === 'root') {
      subscriptionLockedOut.value = null
      return true
    }
    try {
      const orgResponse = await organizationsApi.get(orgToLoad)
      organization.value = orgResponse.data
      if (orgResponse.data?.subscription?.pendingUserSelection && user.value?.role !== 'owner') {
        subscriptionLockedOut.value = PENDING_SELECTION_MESSAGE
        return false
      }
      subscriptionLockedOut.value = null
      return true
    } catch (error: any) {
      const code = error?.response?.status === 403 ? error.response.data?.code : undefined
      if (code === 'subscription_locked_out' || code === 'subscription_pending_selection') return false
      throw error
    }
  }

  // Cross-tab sync: when another tab logs out, mirror it here.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key === 'accessToken' && !e.newValue) {
        user.value = null
        organization.value = null
        accessToken.value = null
        subscriptionLockedOut.value = null
      }
    })
  }

  const login = async (email: string, password: string, rememberMe?: boolean) => {
    loading.value = true
    try {
      const response = await authService.login(email, password, rememberMe)
      accessToken.value = response.accessToken
      user.value = response.user
      localStorage.setItem('accessToken', response.accessToken)

      // Multi-org: boot into the user's primary org and load their org list.
      setActiveOrg(response.user.orgId)
      fetchMyOrganizations()

      // Fetch organization data (skip for root user - orgId is '*')
      if (response.user.orgId && response.user.orgId !== '*' && response.user.role !== 'root') {
        try {
          const orgResponse = await organizationsApi.get(response.user.orgId)
          organization.value = orgResponse.data

          // Proactive lockout detection
          if (orgResponse.data?.subscription?.pendingUserSelection && response.user.role !== 'owner') {
            subscriptionLockedOut.value = PENDING_SELECTION_MESSAGE
          }
        } catch (orgError: any) {
          // If subscription lockout was set by interceptor, don't throw — let the overlay handle it
          if (subscriptionLockedOut.value) {
            return response
          }
          throw orgError
        }
      }

      return response
    } finally {
      loading.value = false
    }
  }

  const demoLogin = async () => {
    loading.value = true
    try {
      const response = await authService.demoLogin()
      accessToken.value = response.accessToken
      user.value = response.user
      localStorage.setItem('accessToken', response.accessToken)
      // Fetch the demo org so guards have it
      if (response.user.orgId) {
        try {
          const orgResponse = await organizationsApi.get(response.user.orgId)
          organization.value = orgResponse.data
        } catch (err) {
          console.warn('[auth] demo org hydration failed', err)
        }
      }
      return response
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      user.value = null
      organization.value = null
      accessToken.value = null
      subscriptionLockedOut.value = null
      memberships.value = []
      setActiveOrg(null)
      localStorage.removeItem('accessToken')
    }
  }

  const updateUser = (updatedUser: { [K in keyof User]?: User[K] | undefined }) => {
    if (user.value) {
      // Handle avatar separately to allow setting to undefined
      const newUser = { ...user.value }
      for (const key in updatedUser) {
        const k = key as keyof User
        if (updatedUser[k] !== undefined) {
          (newUser as any)[k] = updatedUser[k]
        } else if (k === 'avatar') {
          // Allow avatar to be set to undefined
          delete (newUser as any).avatar
        }
      }
      user.value = newUser
    }
  }

  /** Load the orgs the user belongs to and make sure an active org is selected. */
  const fetchMyOrganizations = async () => {
    try {
      memberships.value = await authService.listMyOrganizations()
      const valid = activeOrgId.value && memberships.value.some((m) => m.orgId === activeOrgId.value)
      if (!valid) {
        const primary = user.value?.orgId && memberships.value.some((m) => m.orgId === user.value!.orgId)
          ? user.value!.orgId
          : memberships.value[0]?.orgId
        if (primary) setActiveOrg(primary)
      }
    } catch {
      // non-fatal — switcher just won't populate
    }
  }

  /** Switch the active org (header change, no re-login) and refresh role + org. */
  const switchOrg = async (orgId: string) => {
    if (!orgId || orgId === activeOrgId.value) return
    setActiveOrg(orgId)
    try {
      // /auth/me now reflects the active org's role (server resolves it from the header).
      user.value = await authService.getCurrentUser()
      const orgResponse = await organizationsApi.get(orgId)
      organization.value = orgResponse.data
    } catch (e) {
      console.error('Failed to switch organization:', e)
    }
  }

  /** Create a new org, then switch into it. */
  const createOrg = async (name: string) => {
    const newOrg = await authService.createOrganization(name)
    await fetchMyOrganizations()
    await switchOrg(newOrg.orgId)
    return newOrg
  }

  /**
   * Pick a fallback org (≠ the one being left/deleted) and make it active. We set
   * the active org BEFORE any further request, because the x-org-id header for a
   * just-removed org would 401 against the membership check.
   */
  const swapAwayFrom = async (orgId: string, preferredId?: string | null) => {
    if (orgId !== activeOrgId.value) return
    const fallback = preferredId || memberships.value.find((m) => m.orgId !== orgId)?.orgId || null
    if (!fallback) return
    setActiveOrg(fallback)
    try {
      user.value = await authService.getCurrentUser()
      const orgResponse = await organizationsApi.get(fallback)
      organization.value = orgResponse.data
    } catch (e) {
      console.error('Failed to load fallback organization:', e)
    }
  }

  /** Leave an org you belong to; if it was active, fall back to another. */
  const leaveOrg = async (orgId: string) => {
    const res = await authService.leaveOrganization(orgId)
    await swapAwayFrom(orgId, res.newActiveOrgId)
    await fetchMyOrganizations()
    return res
  }

  /** Transfer ownership to another member; refresh our own role if it's active. */
  const transferOwnership = async (orgId: string, newOwnerUserId: string) => {
    const res = await authService.transferOwnership(orgId, newOwnerUserId)
    await fetchMyOrganizations()
    if (orgId === activeOrgId.value) {
      try {
        user.value = await authService.getCurrentUser()
      } catch (e) {
        console.error('Failed to refresh role after transfer:', e)
      }
    }
    return res
  }

  /** Delete an org (owner-only, non-personal); if it was active, fall back. */
  const deleteOrg = async (orgId: string) => {
    const res = await authService.deleteOrganization(orgId)
    await swapAwayFrom(orgId)
    await fetchMyOrganizations()
    return res
  }

  return {
    user,
    organization,
    accessToken,
    loading,
    memberships,
    activeOrgId,
    activeOrg,
    hasMultipleOrgs,
    fetchMyOrganizations,
    switchOrg,
    createOrg,
    leaveOrg,
    transferOwnership,
    deleteOrg,
    setActiveOrg,
    orgViewVersion,
    bumpOrgView,
    isAuthenticated,
    isRootUser,
    isSuperAdmin,
    isAdmin,
    isOwner,
    isMember,
    isKam,
    isMemberOrKam,
    canManagePipelines,
    canAccessSettings,
    subscriptionLockedOut,
    ensureOrganizationLoaded,
    retryLockout,
    initializeAuth,
    login,
    demoLogin,
    logout,
    updateUser
  }
})
